const axios = require('axios');
const axiosRetry = require('axios-retry').default;
const isRetryAllowed = require('is-retry-allowed');
const createAuthRefreshInterceptor = require('axios-auth-refresh').default;
const { v4: uuidv4 } = require('uuid');

const EVENT = 'web:refreshToken';
const TIMEOUT = 5000;
const HEADER = 'authorization';
const REFRESH_TOKEN_STATUS = [401, 403];
const CREATE_FN = axios.create;
const promises = {};

if (isClient()) {
  window.addEventListener(EVENT, resolvePromise);
}

function isClient() {
  return typeof window !== 'undefined';
}

function isAndroid() {
  return Boolean(window?.TokenExpirationWebInterface);
}

function isIos() {
  return Boolean(
    window?.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.TokenExpirationWebInterface,
  );
}

function refreshAuthPlatform(failedRequest) {
  if (isAndroid() || isIos()) {
    return new Promise((resolve, reject) => {
      const promiseId = uuidv4();
      const timeout = setTimeout(
        () =>
          resolvePromise({
            detail: {
              promiseId,
              token: null,
              error: 'Refresh token timed out',
            },
          }),
        TIMEOUT,
      );

      promises[promiseId] = { resolve, reject, timeout, failedRequest };
      if (isAndroid()) {
        window.TokenExpirationWebInterface.refreshToken(promiseId);
      } else {
        window.webkit.messageHandlers.TokenExpirationWebInterface.postMessage({
          promiseId,
        });
      }
    });
  } else {
    return Promise.reject();
  }
}

function resolvePromise({ detail }) {
  if (promises[detail.promiseId]) {
    const promise = promises[detail.promiseId];

    clearTimeout(promise.timeout);

    if (detail.error || !detail.token) {
      promise.reject(detail);
    } else {
      promise.failedRequest.response.config.headers[HEADER] = detail.token;
      promise.resolve();
    }
    delete promises[detail.promiseId];
  }
}

function isNetworkError(error) {
  return (
    !error.response &&
    Boolean(error.code) && // Prevents retrying cancelled requests
    error.code !== 'ECONNABORTED' && // Prevents retrying timed out requests
    isRetryAllowed(error) // Prevents retrying unsafe errors
  );
}

function injectInterceptors(axiosInstance) {
  axiosRetry(axiosInstance, {
    retries: 3,
    retryCondition: isNetworkError,
    retryDelay: axiosRetry.exponentialDelay,
  });

  if (isClient()) {
    createAuthRefreshInterceptor(axiosInstance, refreshAuthPlatform, {
      statusCodes: REFRESH_TOKEN_STATUS,
      retryInstance: axiosInstance,
    });
  }
}

function initialize(props) {
  const newInstance = CREATE_FN(props);
  injectInterceptors(newInstance);
  return newInstance;
}

injectInterceptors(axios);

axios.create = initialize;

module.exports = axios;
module.exports.default = axios;
